import moment from 'moment';

import {
  AccessLevel,
  AccessLevelJson,
  newAccessLevel,
} from '../../authentication/models/security.model';
import { FormioSubmission, newFormioSubmission } from '../../documents/models/formio.model';
import { DocumentTypesEnum } from 'app/shared/documents/models/document-types.enum';

export enum FormStatus {
  noStatus = -1,
  toFill = 1,
  completed = 2,
  signOff = 3,
  toSign = 4,
}

export enum FormsStatusFilters {
  all,
  toFill,
  toSign,
  videos,
  toRead,
}

export interface Chartnote {
  chartNoteId: number;
  formioKey: string;
  formioSubmissionKey: string;
  formioTemplateVersion?: number;
  formName: string;
  thumbnailUrl: string;
  pdfUri: string;
  statusId: FormStatus;
  chartNoteDate: Date;
  isActiveInvitation: boolean;
  clientAccessLevel: AccessLevel;
  createdBy: string;
  practitioner: string;
  clientCaseId: number;
  isSignedOff: boolean;
  isSigned: boolean | null;
  documentTypeId: DocumentTypesEnum;
  isSignOffAfterClientCompletion: boolean;
}

export interface ChartnoteJson {
  chartNoteId: number;
  formioKey: string;
  formioSubmissionKey: string;
  formioTemplateVersion?: number;
  documentDescription: string;
  thumbnailAccessURL: string;
  pdfUri: string;
  chartNoteStatus: number;
  chartNoteDate: string;
  isActiveInvitation: boolean;
  clientAccessLevel: AccessLevelJson;
  createdBy: string;
  practitioner: string;
  clientCaseId: number;
  isSignedOff: boolean;
  isSigned: boolean | null;
  documentType: DocumentType;
  isSignOffAfterClientCompletion: boolean;
}

export interface AttachmentJson {
  chartNoteId: number;
  formioKey: string;
  formioSubmissionKey: string;
  formioTemplateVersion?: number;
  documentDescription: string;
  thumbnailAccessURL: string;
  pdfURI: string;
  chartNoteStatus: number;
  chartNoteDate: string;
  isActiveInvitation: boolean;
  clientAccessLevel: AccessLevelJson;
  createdBy: string;
  practitioner: string;
  clientCaseId: number;
  isSignedOff: boolean;
  isSigned: boolean | null;
  documentTypeId: number;
  isSignOffAfterClientCompletion: boolean;
}

export function newChartnote(form?: Partial<Chartnote>): Chartnote {
  return {
    chartNoteId: 0,
    formioKey: '',
    formioSubmissionKey: '',
    formioTemplateVersion: undefined,
    formName: '',
    thumbnailUrl: '',
    pdfUri: '',
    statusId: FormStatus.noStatus,
    chartNoteDate: moment().toDate(),
    isActiveInvitation: false,
    clientAccessLevel: newAccessLevel(),
    createdBy: '',
    practitioner: '',
    clientCaseId: 0,
    isSignedOff: false,
    isSigned: null,
    documentTypeId: DocumentTypesEnum.Unknown,
    isSignOffAfterClientCompletion: false,
    ...form,
  };
}

export interface ClientChartnoteJson {
  chartNoteId: number;
  clinicId: number;
  clientId: number;
  clientCaseId: number;
  appointmentId: number;
  chartNoteTypeId: number;
  signedOffEntityId?: number;
  practitionerId: number;
  chartNoteDate: string;
  appointmentDateTime: string;
  documentDescription: string;
  practitioner: string;
  caseName: string;
  documentType: DocumentType;
  clientAccessLevel: ClientAccessLevel;
  formioKey: string;
  formioSubmissionKey: string;
  pdfUri?: string;
  thumbnailUri?: string;
  category: string;
  chartNoteType: string;
  chartNoteStatus: number;
  isCompleted: boolean;
  isPinned: boolean;
  isSignedOff: boolean;
  mustRead: boolean;
  thumbnailAccessURL?: string;
  isActiveInvitation: boolean;
  signedOffDateTimeUtc: string;
  submission: string;
  formioTemplateVersion: number;
  isSigned: boolean | null;
  isSignOffAfterClientCompletion: boolean;
  jsonData: string;
  htmlSource: string;
}

export interface ClientChartnote {
  chartNoteId: number;
  clinicId: number;
  clientId: number;
  clientCaseId: number;
  appointmentId: number;
  chartNoteTypeId: number;
  signedOffEntityId?: number;
  practitionerId: number;
  chartNoteDate: Date;
  documentDescription: string;
  practitioner: string;
  caseName: string;
  documentType: DocumentType;
  clientAccessLevel: ClientAccessLevel;
  formioKey: string;
  formioSubmissionKey: string;
  pdfUri?: string;
  thumbnailUri?: string;
  category: string;
  chartNoteType: string;
  chartNoteStatus: number;
  isCompleted: boolean;
  isPinned: boolean;
  isSignedOff: boolean;
  mustRead: boolean;
  thumbnailAccessURL?: string;
  isActiveInvitation: boolean;
  signedOffDateTimeUtc: Date;
  submission: FormioSubmission;
  formioTemplateVersion?: number;
  isSigned: boolean | null;
  isSignOffAfterClientCompletion: boolean;
  jsonData: string;
  htmlSource: string;
}

interface DocumentType {
  id: number;
  name: string;
}

interface ClientAccessLevel {
  id: number;
  name: string;
}

export interface ChartnoteThumbnails {
  [key: number]: string;
}

export function newDocumentType(type?: Partial<DocumentType>): DocumentType {
  return {
    id: 0,
    name: '',
    ...type,
  };
}

function newClientAccessLevel(accessLevel?: Partial<ClientAccessLevel>): ClientAccessLevel {
  return {
    id: 0,
    name: '',
    ...accessLevel,
  };
}

export function newClientChartnote(clientChartnote?: Partial<ClientChartnote>): ClientChartnote {
  return {
    chartNoteId: 0,
    clinicId: 0,
    clientId: 0,
    clientCaseId: 0,
    appointmentId: 0,
    chartNoteTypeId: 0,
    signedOffEntityId: undefined,
    practitionerId: 0,
    chartNoteDate: moment().toDate(),
    documentDescription: '',
    practitioner: '',
    caseName: '',
    documentType: newDocumentType(),
    clientAccessLevel: newClientAccessLevel(),
    formioKey: '',
    formioSubmissionKey: '',
    pdfUri: '',
    thumbnailUri: '',
    category: '',
    chartNoteType: '',
    chartNoteStatus: 0,
    isCompleted: false,
    isPinned: false,
    isSignedOff: false,
    mustRead: false,
    thumbnailAccessURL: '',
    isActiveInvitation: false,
    signedOffDateTimeUtc: moment().toDate(),
    submission: newFormioSubmission(),
    formioTemplateVersion: 0,
    isSigned: null,
    isSignOffAfterClientCompletion: false,
    jsonData: '',
    htmlSource: '',
    ...clientChartnote,
  };
}
